import React, { useRef, useState, useContext, useCallback, useEffect } from 'react';
import axios from "axios";

import { InputText } from 'primereact/inputtext';
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { Button } from 'primereact/button';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from 'primereact/toast';

import { BarrioContext } from "../components/BarrioContext";
import { useAuth } from "../components/AuthContext";
import { useTerminologia } from "../hooks/useTerminologia";

const TIPO_PAGO_COLORES = {
  Parcial:  { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
  Total:    { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
  Anticipo: { bg: '#eff8ff', color: '#175cd3', border: '#b2ddff' },
};

const PagoProveedores = () => {
  const toast = useRef(null);
  const { barrio } = useContext(BarrioContext);
  const { administracionId } = useAuth();
  const t = useTerminologia();

  const HOST_API = process.env.REACT_APP_URL;

  const formatDate = (date) => {
    if (!date) return "";
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const urlComprobante = (url) => `${HOST_API}${url}`.replace(/([^:])\/\/+/g, '$1/');

  // ---- combos ----
  const [proveedoresTodos, setProveedoresTodos] = useState([]);
  const [tiposPago, setTiposPago] = useState([]);

  useEffect(() => {
    axios.get(`${HOST_API}api/pagoProveedor/getTiposPago`)
      .then((res) => setTiposPago(res.data))
      .catch((err) => console.error("Error al obtener tipos de pago:", err));

    axios.get(`${HOST_API}api/proveedores/GetProveedoresCombo?barrioId=${barrio.id}&administracionId=${administracionId}&grupoGastoId=0`)
      .then((res) => setProveedoresTodos(res.data.map((p) => ({ ...p, proveedorId: Number(p.proveedorId) }))))
      .catch((err) => console.error("Error al obtener proveedores:", err));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [administracionId, barrio.id]);

  // ---- Consulta de Comprobantes ----
  const [pagos, setPagos] = useState([]);
  const [loadingPagos, setLoadingPagos] = useState(false);
  const [filtroFechaDesde, setFiltroFechaDesde] = useState(null);
  const [filtroFechaHasta, setFiltroFechaHasta] = useState(null);
  const [filtroProveedorId, setFiltroProveedorId] = useState(null);
  const [filtroTipoPagoId, setFiltroTipoPagoId] = useState(null);
  const [filtroNroFactura, setFiltroNroFactura] = useState("");

  const fetchPagos = useCallback(async () => {
    if (!barrio?.id) return;
    setLoadingPagos(true);
    try {
      const params = new URLSearchParams();
      params.append("administracionId", administracionId);
      params.append("barrioId", barrio.id);
      if (filtroFechaDesde) params.append("fechaDesde", formatDate(filtroFechaDesde));
      if (filtroFechaHasta) params.append("fechaHasta", formatDate(filtroFechaHasta));
      if (filtroProveedorId) params.append("proveedorId", filtroProveedorId);
      if (filtroNroFactura) params.append("nroFactura", filtroNroFactura);

      const requests = [];

      // El filtro "Tipo de Pago" (Parcial/Total/Anticipo) solo aplica a los pagos
      // a proveedor: si esta activo, se excluyen los pagos de gastos porque no
      // tienen ese concepto (usan forma de pago, no tipo de pago).
      const paramsProveedor = new URLSearchParams(params);
      if (filtroTipoPagoId) paramsProveedor.append("tipoPagoProveedorId", filtroTipoPagoId);
      requests.push(
        axios.get(`${HOST_API}api/pagoProveedor/getPagos?${paramsProveedor.toString()}`)
          .then((res) => (res.data || []).map((p) => ({ ...p, origen: "proveedor", id: `proveedor-${p.pagoProveedorId}` })))
      );

      if (!filtroTipoPagoId) {
        requests.push(
          axios.get(`${HOST_API}api/pagoGasto/GetPagosGastoPorBarrio?${params.toString()}`)
            .then((res) => (res.data || []).map((p) => ({ ...p, origen: "gasto", id: `gasto-${p.pagoGastoId}` })))
        );
      }

      const resultados = await Promise.all(requests);
      const unificado = resultados.flat().sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago));
      setPagos(unificado);
    } catch (err) {
      console.error("Error al obtener pagos:", err);
    } finally {
      setLoadingPagos(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [HOST_API, administracionId, barrio?.id]);

  useEffect(() => { fetchPagos(); }, [fetchPagos]);

  const handleEliminarPago = (row) => {
    confirmDialog({
      message: `¿Eliminar el pago a ${row.nombreProveedor} por $${Number(row.monto).toLocaleString("es-AR")}?`,
      header: 'Confirmación',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Eliminar',
      rejectLabel: 'Cancelar',
      accept: async () => {
        try {
          if (row.origen === "gasto") {
            await axios.post(`${HOST_API}api/pagoGasto/AnularPago`, null, {
              params: { pagoGastoId: row.pagoGastoId, administracionId },
            });
          } else {
            await axios.delete(`${HOST_API}api/pagoProveedor/deletePago/${row.pagoProveedorId}/${administracionId}`);
          }
          toast.current.show({ severity: "success", summary: "Pago a Proveedores", detail: "Pago eliminado", life: 2000 });
          fetchPagos();
        } catch (err) {
          toast.current.show({ severity: "error", summary: "Error", detail: "No se pudo eliminar el pago", life: 2500 });
        }
      },
    });
  };

  const tipoPagoTemplate = (row) => {
    const desc = row.tipoPagoDescripcion || "";
    const clave = Object.keys(TIPO_PAGO_COLORES).find((k) => desc.includes(k));
    const s = TIPO_PAGO_COLORES[clave] || { bg: '#f2f4f7', color: '#344054', border: '#d0d5dd' };
    return (
      <span style={{ background: s.bg, color: s.color, border: `1px solid ${s.border}`, fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99 }}>
        {row.tipoPagoDescripcion}
      </span>
    );
  };

  const origenTemplate = (row) => (
    <span style={{
      background: row.origen === "gasto" ? '#f4f3ff' : '#eff8ff',
      color: row.origen === "gasto" ? '#6938ef' : '#175cd3',
      border: `1px solid ${row.origen === "gasto" ? '#d9d6fe' : '#b2ddff'}`,
      fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
    }}>
      {row.origen === "gasto" ? "Gasto" : "Proveedor"}
    </span>
  );

  if (!barrio?.id) {
    return (
      <div>
        <h4>Pago a Proveedores</h4>
        <p>Elegí un {t.barrio} en el selector de la barra superior para continuar.</p>
      </div>
    );
  }

  return (
    <>
      <ConfirmDialog />
      <Toast ref={toast} />

      <h4>Pago a Proveedores</h4>

      {/* ── Consulta de Comprobantes ── */}
      <h5>Consulta de Comprobantes de Pago</h5>
      <div className="flex flex-wrap align-items-end gap-3 mb-3">
        <div>
          <label className="block mb-1">Desde</label>
          <Calendar value={filtroFechaDesde} onChange={(e) => setFiltroFechaDesde(e.value)} showIcon showButtonBar dateFormat="dd/mm/yy" className="p-inputtext-sm" />
        </div>
        <div>
          <label className="block mb-1">Hasta</label>
          <Calendar value={filtroFechaHasta} onChange={(e) => setFiltroFechaHasta(e.value)} showIcon showButtonBar dateFormat="dd/mm/yy" className="p-inputtext-sm" />
        </div>
        <div style={{ minWidth: 200 }}>
          <label className="block mb-1">Proveedor</label>
          <Dropdown value={filtroProveedorId} options={proveedoresTodos} optionValue="proveedorId" optionLabel="nombreProveedor" onChange={(e) => setFiltroProveedorId(e.value)} placeholder="Todos" showClear filter className="p-inputtext-sm w-full" />
        </div>
        <div style={{ minWidth: 160 }}>
          <label className="block mb-1">Tipo de Pago</label>
          <Dropdown value={filtroTipoPagoId} options={tiposPago} optionValue="tipoPagoProveedorId" optionLabel="descripcion" onChange={(e) => setFiltroTipoPagoId(e.value)} placeholder="Todos" showClear className="p-inputtext-sm w-full" />
        </div>
        <div>
          <label className="block mb-1">Nro Factura/Comprobante</label>
          <InputText value={filtroNroFactura} onChange={(e) => setFiltroNroFactura(e.target.value)} className="p-inputtext-sm" />
        </div>
        <Button label="Buscar" icon="pi pi-search" onClick={fetchPagos} />
      </div>

      <DataTable value={pagos} loading={loadingPagos} paginator rows={10} emptyMessage="No se encontraron comprobantes de pago." size="small" dataKey="id">
        <Column field="fechaPago" header="Fecha" body={(r) => new Date(r.fechaPago).toLocaleDateString("es-AR")} sortable />
        <Column field="nombreProveedor" header="Proveedor" sortable />
        <Column header="Origen" body={origenTemplate} />
        <Column field="tipoPagoDescripcion" header="Tipo de Pago" body={tipoPagoTemplate} />
        <Column field="monto" header="Monto" body={(r) => `$${Number(r.monto).toLocaleString("es-AR")}`} sortable />
        <Column field="nroFactura" header="Nro Factura" />
        <Column field="detalle" header="Detalle" />
        <Column
          header="Comprobante"
          body={(r) => r.comprobanteUrl
            ? <Button icon="pi pi-file" rounded text severity="info" tooltip="Ver comprobante" onClick={() => window.open(urlComprobante(r.comprobanteUrl), "_blank")} />
            : null}
        />
        <Column
          header="Acciones"
          body={(r) => (
            <Button icon="pi pi-trash" rounded text severity="danger" tooltip="Eliminar" onClick={() => handleEliminarPago(r)} />
          )}
        />
      </DataTable>
    </>
  );
};

export default PagoProveedores;
